import React from 'react'
import { Modal, Button } from '@arnsbogroup/react-voicearchive'

/**
 * Last stop before an order is invoiced with tasks the talent can never be paid
 * for. Finance pays from a list that only contains tasks whose status proves the
 * work landed; these are not on it and never will be, so once the order is closed
 * the fee is lost with nothing anywhere to say so.
 *
 * A warning, not a block - the coordinator may be about to zero the cost, or the
 * talent may genuinely never have delivered. Continuing is written to the order's
 * action log by tbiChange().
 *
 * Deliberately does not pass onSubmit: Modal binds Enter to it, and the risky
 * option must not be one keystroke away.
 *
 * @see documentation/UNPAID_TASK_CONTROLS.md
 */
const MissingUploadsModal = ({ show = false, tasks = [], onConfirm, onClose }) => (
  <Modal
    show={show}
    title="Some talent cannot be paid for this order"
    onClose={onClose}
    onOutsideClick={onClose}
    dialogPanelClasses="max-w-2xl"
  >
    <p className="text-sm text-gray-700">
      {tasks.length === 1 ? 'One task on' : `${tasks.length} tasks on`} this order
      {tasks.length === 1 ? ' has' : ' have'} a cost but will not appear on Finance's
      To Be Paid list, so the talent will not be paid. Fix the tasks below first, or
      invoice anyway if that is intentional.
    </p>

    <div className="mt-4 max-h-80 overflow-y-auto rounded border">
      <table className="w-full text-left text-xs">
        <thead className="bg-gray-50 text-gray-500">
          <tr>
            <th className="px-3 py-2 font-medium">Task</th>
            <th className="px-3 py-2 font-medium">Type</th>
            <th className="px-3 py-2 font-medium">Talent</th>
            <th className="px-3 py-2 text-right font-medium">Cost</th>
            <th className="px-3 py-2 font-medium">Why</th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((task) => (
            <tr key={task.task_uid} className="border-t">
              <td className="px-3 py-2">
                <a
                  href={`/task/${task.task_uid}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-primary"
                >
                  #{task.task_uid}
                </a>
              </td>
              <td className="px-3 py-2">{task.type_shortname || task.type_name}</td>
              <td className="px-3 py-2">{task.supplier_name}</td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                {task.cost} {task.cost_currency}
              </td>
              <td className="px-3 py-2 text-red-600">{task.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <div className="mt-6 flex justify-end gap-2">
      <Button variant="solid" size="sm" onClick={onClose}>
        Go back and fix
      </Button>
      <Button variant="outline" state="negative" size="sm" onClick={onConfirm}>
        Invoice anyway
      </Button>
    </div>
  </Modal>
)

export default MissingUploadsModal
